import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { fmt, fmtPct, brDateShort } from "./util";

const ESCALA_LABEL = { dia: "por dia", semana: "por semana (início na segunda)", mes: "por mês" };

function rotulo(periodo, escala) {
  if (escala === "mes") return `${periodo.slice(5, 7)}/${periodo.slice(0, 4)}`;
  return brDateShort(periodo);
}

export function EvolucaoProducao({ evolucao, filtros }) {
  const abas = Object.keys(evolucao.series || {});
  const [aba, setAba] = useState(abas[0] || "");
  const ativa = abas.includes(aba) ? aba : abas[0];
  const dados = useMemo(() => (evolucao.series[ativa] || []).map((d) => ({ ...d, rotulo: rotulo(d.periodo, evolucao.escala) })), [evolucao, ativa]);
  const total = dados.reduce((s, d) => s + d.quantidade, 0);
  const unidade = ativa ? ativa.match(/\((.*)\)$/)?.[1] : "";
  return (
    <div className="pcp-card" style={{ padding: 20 }} data-testid="evolucao-producao">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <div className="overline">Produção e avanço</div>
          <h2 className="font-display font-bold text-lg mt-1">Evolução no período</h2>
          <div style={{ fontSize: "0.75rem", color: "#71717A", marginTop: 2 }}>Apontamentos {ESCALA_LABEL[evolucao.escala]} · fuso Cuiabá · uma atividade/unidade por vez</div>
        </div>
        {ativa && <div className="font-mono" style={{ fontSize: "0.85rem" }} data-testid="evolucao-total">Total: <strong>{fmt(total)}</strong> <span translate="no">{unidade}</span></div>}
      </div>
      {abas.length === 0 ? (
        <div className="dash-vazio" data-testid="evolucao-vazio">Sem produção registrada no período</div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }} data-testid="evolucao-abas">
            {abas.map((a) => (
              <button key={a} type="button" className="chip-btn" data-active={a === ativa} onClick={() => setAba(a)} data-testid={`evolucao-aba-${a.replace(/[^\w]/g, "_")}`}>{a}</button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dados} margin={{ left: 0, right: 8 }}>
              <CartesianGrid stroke="#F4F4F5" vertical={false} />
              <XAxis dataKey="rotulo" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => fmt(v, 0)} />
              <Tooltip contentStyle={{ borderRadius: 3, border: "1px solid #0A0A0A", fontSize: 12 }} formatter={(v) => [`${fmt(v)} ${unidade}`, ativa]} />
              <Bar dataKey="quantidade" fill="#FFCC00" stroke="#0A0A0A" />
            </BarChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

function LinhaAtividade({ g }) {
  const [aberto, setAberto] = useState(false);
  const pct = g.pct == null ? 0 : Math.min(100, g.pct);
  const key = `${g.categoria}-${g.unidade}`;
  return (
    <div className="dash-atividade" data-testid={`execplan-${key}`}>
      <button type="button" className="dash-atividade-head" onClick={() => setAberto(!aberto)} data-testid={`execplan-toggle-${key}`} aria-expanded={aberto}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <strong>{g.atividade}</strong>
            <span style={{ fontSize: "0.72rem", color: "#71717A" }}>{g.itens.length} item(ns)</span>
          </div>
          <div className="progress-track" style={{ marginTop: 6, height: 6 }}><div className="progress-fill" style={{ width: `${pct}%`, background: g.pct > 100 ? "#DC2626" : undefined }} /></div>
        </div>
        <div className="dash-atividade-nums">
          <div className="font-mono" data-testid={`execplan-valores-${key}`}><strong>{fmt(g.executado)}</strong> <span style={{ color: "#71717A" }}>/ {fmt(g.planejado)} <span translate="no">{g.unidade}</span></span></div>
          <div style={{ fontSize: "0.72rem", color: "#52525B" }}>{fmtPct(g.pct)} acumulado · <span data-testid={`execplan-periodo-${key}`}>{fmt(g.executado_periodo)} {g.unidade} no período</span></div>
        </div>
        {aberto ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
      </button>
      {aberto && (
        <div className="dash-table-wrap" style={{ marginTop: 8 }}>
          <table className="pcp-table dash-table-itens" data-testid={`execplan-itens-${key}`}>
            <thead><tr><th>OS</th><th>Item</th><th>Un. orig.</th><th style={{ textAlign: "right" }}>Planejado</th><th style={{ textAlign: "right" }}>Executado</th><th style={{ textAlign: "right" }}>No período</th><th style={{ textAlign: "right" }}>%</th></tr></thead>
            <tbody>
              {g.itens.map((it) => (
                <tr key={it.item_id}>
                  <td data-label="OS"><Link to={`/ordens/${it.os_id}`} className="font-mono" style={{ textDecoration: "underline" }}>{it.os_numero}</Link></td>
                  <td data-label="Item" style={{ maxWidth: 320 }}>{it.descricao}</td>
                  <td data-label="Un." className="font-mono" translate="no">{it.unidade_original}</td>
                  <td data-label="Planejado" className="font-mono" style={{ textAlign: "right" }}>{fmt(it.planejado)}</td>
                  <td data-label="Executado" className="font-mono" style={{ textAlign: "right" }}>{fmt(it.executado)}</td>
                  <td data-label="No período" className="font-mono" style={{ textAlign: "right" }}>{fmt(it.executado_periodo)}</td>
                  <td data-label="%" className="font-mono" style={{ textAlign: "right", color: it.pct > 100 ? "#DC2626" : undefined }}>{fmtPct(it.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ExecutadoPlanejado({ execPlanejado, filtros }) {
  const grupos = execPlanejado.filter((g) => g.planejado > 0 || g.executado > 0);
  return (
    <div className="pcp-card" style={{ padding: 20 }} data-testid="exec-planejado">
      <div style={{ marginBottom: 12 }}>
        <div className="overline">Executado × Planejado</div>
        <h2 className="font-display font-bold text-lg mt-1">Por atividade e unidade</h2>
        <div style={{ fontSize: "0.75rem", color: "#71717A", marginTop: 2 }}>
          <strong>Planejado</strong> = total dos itens das OS do recorte (não é meta do período) · <strong>Executado</strong> = acumulado dos itens · <strong>No período</strong> = apontamentos {brDateShort(filtros.inicio)}–{brDateShort(filtros.fim)}
        </div>
      </div>
      {grupos.length === 0 ? (
        <div className="dash-vazio" data-testid="exec-planejado-vazio">Nenhum item de serviço nas OS do recorte</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>{grupos.map((g) => <LinhaAtividade key={`${g.categoria}-${g.unidade}`} g={g} />)}</div>
      )}
    </div>
  );
}
